import { useMemo } from 'react';
import { CanvasViewer } from '@/features/canvas/components/canvas-viewer';
import { RichTextViewer } from '@/features/editor/components/rich-text-viewer';
import type { CanvasDiffResult, DiffRange } from '@/lib/diff/types';
import * as Y from 'yjs';
import type { Awareness } from 'y-protocols/awareness';

// Minimal awareness stub — snapshot is frozen, no collaboration needed
function makeFakeAwareness(): Awareness {
  const listeners = new Map<string, Set<() => void>>();
  const states = new Map();
  return {
    clientID: 0,
    states,
    getLocalState: () => null,
    setLocalState: () => {},
    setLocalStateField: () => {},
    getStates: () => states,
    on(event: string, cb: () => void) {
      if (!listeners.has(event)) listeners.set(event, new Set());
      listeners.get(event)!.add(cb);
    },
    off(event: string, cb: () => void) {
      listeners.get(event)?.delete(cb);
    },
    emit(event: string) {
      listeners.get(event)?.forEach((cb) => cb());
    },
    destroy() {},
  } as unknown as Awareness;
}

export interface SnapshotViewerProps {
  /** Pre-populated Y.Doc (apply state before passing in). */
  yDoc: Y.Doc;
  /** Unique namespace — used as entityId passthrough for canvas. */
  namespace: string;
  entityKind: 'document' | 'canvas';
  /** Document diff ranges — passed to RichTextViewer when provided. */
  diffRanges?: DiffRange[];
  /** Canvas node diff result — passed to CanvasViewer when provided. */
  canvasDiffResult?: CanvasDiffResult;
  /** Forwarded to CanvasViewer — fits to content on first mount. Canvas-only. */
  fitOnMount?: boolean;
}

export function SnapshotViewer({ yDoc, namespace, entityKind, diffRanges, canvasDiffResult, fitOnMount }: SnapshotViewerProps) {
  // Canvas path — delegate to CanvasViewer with a stub awareness
  if (entityKind === 'canvas') {
    const fakeAwareness = makeFakeAwareness();
    return (
      <CanvasViewer
        yDoc={yDoc}
        awareness={fakeAwareness}
        entityId={namespace}
        diffResult={canvasDiffResult}
        fitOnMount={fitOnMount}
      />
    );
  }

  // Document path — Tiptap read-only viewer (mode B: injected yDoc + awareness).
  return <SnapshotDocumentViewer yDoc={yDoc} diffRanges={diffRanges} />;
}

// Internal document-only viewer using RichTextViewer in mode B
function SnapshotDocumentViewer({
  yDoc,
  diffRanges,
}: {
  yDoc: Y.Doc;
  diffRanges?: DiffRange[];
}) {
  const fakeAwareness = useMemo(makeFakeAwareness, []);

  return (
    <RichTextViewer
      doc={yDoc}
      awareness={fakeAwareness}
      diffRanges={diffRanges}
    />
  );
}
